import { Injectable } from '@angular/core';
import Swal, { SweetAlertIcon, SweetAlertResult } from 'sweetalert2';
import { AlertType } from '../../enums/sweet-alert/sweet-alert.enum';

interface QueuedAlert {
  title: string;
  text: string;
  icon: SweetAlertIcon;
}

@Injectable({
    providedIn: 'root',
})
export class SweetAlertService {
    private notificationQueue: QueuedAlert[] = [];
    private isAlertActive = false;

    async mixin(text: string, icon: SweetAlertIcon, timer: number = 10000) {
        const Toast = Swal.mixin({
            icon: icon,
            toast: true,
            position: 'top-end',
            timer: timer,
            timerProgressBar: true,
            showConfirmButton: false,
        });

        return await Toast.fire(text);
    }

    public async mixinAlert(
        title: string,
        text: string,
        type: AlertType,
        timer: number = 10000
    ): Promise<SweetAlertResult<any>> {
        let icon: SweetAlertIcon;
        switch (type) {
            case AlertType.Information:
                icon = 'info';
                break;
            case AlertType.Warning:
                icon = 'warning';
                break;
            case AlertType.Danger:
                icon = 'error';
                break;
            case AlertType.Urgent:
                icon = 'question';
                break;
            case AlertType.Success:
                icon = 'success';
                break;
            default:
                icon = 'info';
                break;
        }

        const Toast = Swal.mixin({
            icon: icon!,
            toast: true,
            position: 'center',
            timer: timer,
            timerProgressBar: true,
            showConfirmButton: false,
            title: title,
            text: text,
        });

        return await Toast.fire(title);
    }

    public async customDialog(): Promise<any> {
        await Swal.fire({
            position: 'top-end',
            icon: 'success',
            title: 'Your work has been saved',
            showConfirmButton: false,
            timer: 10000,
        });
    }

    public confirmToast(
        title: string,
        text: string,
        icon: SweetAlertIcon,
        confirmBtn: string,
        cancelBtn: string,
        timer?: number
    ): Promise<SweetAlertResult<any>> {
        const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btn bg-gradient-info ms-2 mb-0 js-btn-next mt-2 mr',
                cancelButton:
                    'btn bg-gradient-warning ms-auto mb-0 js-btn-next  mt-2  mr',
            },
            buttonsStyling: false,
        });

        return swalWithBootstrapButtons.fire({
            title: title,
            text: text,
            icon: icon,
            timer: timer,
            timerProgressBar: true,
            showCancelButton: true,
            confirmButtonText: confirmBtn,
            cancelButtonText: cancelBtn,
            reverseButtons: true,
        });
    }

    public confirmToastSupervision(
        title: string,
        text: string,
        icon: SweetAlertIcon,
        confirmBtn: string,
        cancelBtn: string,
        timer?: number
    ): Promise<SweetAlertResult<any>> {
        const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-light-primary',
                cancelButton: 'btn btn-light-danger',
            },
            buttonsStyling: false,
        });

        return swalWithBootstrapButtons.fire({
            title: title,
            text: text,
            icon: icon,
            timer: timer,
            timerProgressBar: true,
            showCancelButton: true,
            confirmButtonText: confirmBtn,
            cancelButtonText: cancelBtn,
            reverseButtons: true,
        });
    }

    public confirmToastGeneric(
        title: string,
        text: string,
        icon: SweetAlertIcon,
        confirmBtn: string,
        cancelBtn: string,
        timer?: number
    ): Promise<SweetAlertResult<any>> {
        return Swal.fire({
            title: title,
            text: text,
            icon: icon,
            timer: timer,
            timerProgressBar: true,
            showCancelButton: true,
            confirmButtonText: confirmBtn,
            cancelButtonText: cancelBtn,
            reverseButtons: true,
        });
    }

    confirmThree(
        title: string,
        text: string,
        icon: SweetAlertIcon,
        confirmBtn: string,
        denyBtn: string,
        cancelBtn: string,
        timer?: number
    ): Promise<SweetAlertResult<any>> {
        const swalWithBootstrapButtons = Swal.mixin({
            customClass: {
                confirmButton: 'btn btn-light-success mx-2 mb-0 js-btn-next mt-2',
                denyButton: 'btn btn-light-warning mx-2 mb-0 js-btn-next mt-2',
                cancelButton: 'btn btn-light mx-2 mb-0 js-btn-next mt-2',
            },
            buttonsStyling: false,
        });
        return swalWithBootstrapButtons.fire({
            title: title,
            text: text,
            icon: icon,
            timer: timer,
            timerProgressBar: true,
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: confirmBtn,
            denyButtonText: denyBtn,
            cancelButtonText: cancelBtn,
            reverseButtons: true,
        });
    }

    public messageAlert(
      title: string,
      text: string,
      icon: SweetAlertIcon = 'info'
    ): void {
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
        showCloseButton: true
      });

      Toast.fire({
        icon,
        title,
        text
      });
    }

    public enqueueAlert(title: string, text: string, icon: SweetAlertIcon = 'info') {
      this.notificationQueue.push({
        title,
        text,
        icon
      });

      // Si no hay una alerta activa, comienza la cola
      if (!this.isAlertActive) {
        this.showNextAlert();
      }
    }

    private async showNextAlert() {
      this.isAlertActive = true;

      while (this.notificationQueue.length > 0) {
        const next = this.notificationQueue.shift();

        if (next) {
          await Swal.fire({
            title: next.title,
            text: next.text,
            icon: next.icon,
            showConfirmButton: false,
            showCloseButton: true,
            allowOutsideClick: true,
            allowEscapeKey: true
          });
        }
      }

      this.isAlertActive = false;
    }


    getSwal() {
        return Swal;
    }
}
